import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Milestone } from "lucide-react";

interface MilestoneDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Predefined milestone options with hardcoded colors for fast loading
const PREDEFINED_MILESTONES = [
  { text: "Briefing Completed", color: "#8d70d3" }, // Lilac
  { text: "Project Start", color: "#10B981" }, // Green (same as FINAL status)
  { text: "Feedback Overdue", color: "#374151" }, // Dark Grey (same as ON HOLD status)
  { text: "First Look", color: "#10B981" }, // Green (same as FINAL status)
  { text: "Preview", color: "#10B981" }, // Green (same as FINAL status)
  { text: "Final", color: "#10B981" }, // Green (same as FINAL status)
  { text: "Approved", color: "#8d70d3" }, // Lilac
];

// Default color for custom milestones
const CUSTOM_MILESTONE_COLOR = "#6b7280"; // Gray-500

export function MilestoneDialog({ projectId, open, onOpenChange }: MilestoneDialogProps) {
  const [customText, setCustomText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Auto-focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const createMilestoneMutation = useMutation({
    mutationFn: async (data: { 
      text: string; 
      milestoneColor?: string;
    }) => {
      const milestoneData = {
        projectId,
        pulseDescription: data.text,
        category: 'milestone',
        status: 'new_message',
        milestoneColor: data.milestoneColor,
      };
      
      const response = await apiRequest('POST', '/api/pulses', milestoneData);
      
      if (!response.ok) throw new Error('Failed to create milestone');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      toast({
        title: "Milestone created",
        description: "New milestone has been added successfully.",
      });
      handleClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create milestone. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setCustomText("");
    onOpenChange(false);
  };

  const handlePredefinedClick = (milestone: typeof PREDEFINED_MILESTONES[0]) => {
    createMilestoneMutation.mutate({
      text: milestone.text,
      milestoneColor: milestone.color,
    });
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customText.trim()) return;
    
    createMilestoneMutation.mutate({
      text: customText.trim(),
      milestoneColor: CUSTOM_MILESTONE_COLOR, // Custom milestones get a gray color
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && customText.trim()) {
      handleCustomSubmit(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-gray-900 text-gray-100 border-gray-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Milestone className="w-5 h-5" />
            Create Milestone
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Predefined milestone bubbles */}
          <div className="space-y-2">
            <p className="text-sm text-gray-400">Select a predefined milestone:</p>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_MILESTONES.map((milestone, index) => (
                <button
                  key={index}
                  onClick={() => handlePredefinedClick(milestone)}
                  className="px-3 py-1.5 rounded-full text-white text-sm font-medium hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: milestone.color }}
                >
                  {milestone.text}
                </button>
              ))}
            </div>
          </div>
          
          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gray-900 px-2 text-gray-500">or</span>
            </div>
          </div>
          
          {/* Custom milestone input */}
          <form onSubmit={handleCustomSubmit} className="space-y-2">
            <p className="text-sm text-gray-400">Enter a custom milestone:</p>
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your milestone text..."
                className="flex-1 bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500"
              />
              <Button
                type="submit"
                disabled={!customText.trim() || createMilestoneMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}